import { ChangeEvent, useEffect, useLayoutEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, TextField } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Add } from '@mui/icons-material'
import { PageTitle } from '../components/atoms/PageTitle'
import { callAxios } from '../hooks/useAxios'
import { Device, SystemSetup } from '../types/models'
import { sorter } from '../utils/sorter'
import { DataTable, DataTableColumns } from '../components/atoms/DataTable'
import releases from '../releases.json'
import { AgeLight } from '../components/atoms/AgeLight'
import { CustomSwitch } from '../components/atoms/CustomSwitch'

export function AdminDevicesPage(): React.ReactElement {

  const navigate = useNavigate()
  const [devices, setDevices] = useState<Device[]>([])
  const { t } = useTranslation()
  const [renderedDevices, setRenderedDevices] = useState<Device[]>([])
  const [setup, setSetup] = useState<SystemSetup>({})

  useLayoutEffect(() => {
    setRenderedDevices(devices)
  }, [devices])

  const updateDevices = () => {
    callAxios<Device[]>({
      url: '/devices',
    }).then(res => res.success && setDevices(sorter(res.data, 'hw_id')))
    callAxios<SystemSetup>({
      url: '/setup',
    }).then(res => res.success && setSetup(res.data))
  }

  const fwVersion = (dev: Device): string | undefined => {
    return dev.values?.firmware_version?.toLowerCase()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => updateDevices(), [])

  const rev = releases as Record<string, string>

  const columns: DataTableColumns<Device> = {
    [t('Column-ID')]: 'id',
    [t('Column-HW ID')]: (dev: Device) =>
      <Box sx={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
        <AgeLight since={dev.values?.last_data}/>
        {dev.hw_id}
      </Box>,
    [t('Column-Owner')]: 'owner.email',
    [t('Column-Release')]: (dev: Device) => rev[fwVersion(dev) || ''] || '',
  }

  const handleSearchDeviceById = (event: ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.trim().toLowerCase()
    if (searchValue.length !== 0) {
      const filteredDevices = devices.filter((device) => device.hw_id.toLowerCase().includes(searchValue) || `${device.id}` === searchValue)
      setRenderedDevices(filteredDevices)
    } else {
      setRenderedDevices(devices)
    }
  }

  const onToggleUpdates = () => {
    callAxios<SystemSetup>({
      url: '/setup',
      method: 'PATCH',
    }, { disable_firmware_updates: !setup.disable_firmware_updates }).then(() => {
      setSetup({ ...setup, disable_firmware_updates: !setup.disable_firmware_updates })
    })
  }

  return (
    <Box>
      <PageTitle title={t('Devices')}/>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button data-cy="button-Add new device" variant="outlined" color="secondary" size="large" onClick={() => navigate('/admin/devices/add')} endIcon={<Add/>}>
          <Trans>Add new device</Trans>
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <TextField
            sx={{ '& ::placeholder': { fontSize: '14px' } }}
            margin="normal"
            name="devices-search-bar"
            placeholder={t('Search by hardware ID')}
            type="search"
            id="devices-search-bar"
            size="small"
            onChange={handleSearchDeviceById}
          />
      </Box>

      <DataTable dataCy="table-Admin devices" sx={{ mt: 2 }} data={renderedDevices} columns={columns} link="/admin/devices/:id"/>

      <Box sx={{ mt: 6 }}>
        <CustomSwitch
          disabled={setup.disable_firmware_updates === undefined}
          onClick={onToggleUpdates}
          checked={!!setup.disable_firmware_updates}
        /><Trans>Disable all automatic firmware updates.</Trans>
      </Box>
    </Box>
  )
}
